import React, { useCallback } from 'react';
import cn from 'classnames';
import { isNil } from 'lodash';

import Permitted from 'components/core/Permitted.component';
import Icon from 'components/generic/Icon.component';
import Cell from 'components/report/payment/Cell.component';
import Transfers from 'components/report/payment/Transfers.component';
import PERM from 'constants/core/perm.const';
import PAYMENT_ORIGIN_ALL from 'constants/payment/paymentOriginAll.const';
import PAYMENT_ORIGIN_NAME from 'constants/payment/paymentOriginName.const';
import { useTranslation } from 'providers/GILT.provider';
import { useThemeWithBright } from 'providers/Theme.provider';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import call from 'utils/functions/call.util';

import CN from './PaymentSource.module.scss';

const PaymentSource = ({
  begin,
  end,
  payment,
  hasAmexFee,
  onExpand,
  isExpanded,
  isGroupOpen,
  groupName,
  selectedStylistId,
  hideModalForSubPaymentsGroup,
}) => {
  const { isDarkMode } = useThemeWithBright();
  const { t } = useTranslation();

  const {
    amount,
    total_amount_paid_via_card: totalAmountPaidViaCard,
    count,
    transfers,
    fee_inc_vat: includingVat,
    payment_origin: paymentOrigin,
    transaction_ids: txIds,
    intent_ids: intentIds,
  } = payment ?? {};

  const handleClick = useCallback(
    //
    () => void call('PaymentSource.onExpand', onExpand, { origin: paymentOrigin }),
    [onExpand, paymentOrigin],
  );

  if (isNil(paymentOrigin)) return null;

  return (
    <div
      data-bem="PaymentSource"
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      <div data-bem="PaymentSource__constrictor" className={CN.constrictor}>
        <div
          data-bem="PaymentSource__button"
          role="button"
          tabIndex={0}
          onClick={handleClick}
          className={cn({
            [CN.button]: true,
            [CN.highlight]: isExpanded,
            [CN.selected]: isExpanded,
          })}
        >
          <Cell>
            <div
              className={cn({
                [CN.withIcon]: true,
                [CN.moveCell]: isGroupOpen,
              })}
            >
              <Icon
                tag="SetUpView__arrow"
                variant="roundedPlusMinusGreen"
                selected={!isExpanded}
                unselected={isExpanded}
                width="lg"
              />
              {t(PAYMENT_ORIGIN_NAME[paymentOrigin]) || paymentOrigin}
            </div>
          </Cell>

          <Cell>{count}</Cell>
          <Cell value={totalAmountPaidViaCard} selected={isExpanded} />
          {paymentOrigin === PAYMENT_ORIGIN_ALL.amexFee && hasAmexFee ? (
            <div className={CN.cell}>{t('common.notApplicable')}</div>
          ) : (
            <Cell value={amount} selected={isExpanded} />
          )}

          <Cell value={includingVat} selected={isExpanded} />
          <Cell value={transfers} selected={isExpanded} />
        </div>

        <Permitted value={PERM.canViewDailyCashupAllStaff}>
          {fromAnyToBool(isExpanded) && (
            <Transfers
              permission="all"
              begin={begin}
              end={end}
              ids={txIds}
              intentIds={intentIds}
              groupName={groupName}
              selectedStylistId={selectedStylistId}
              hideModalForSubPaymentsGroup={hideModalForSubPaymentsGroup}
            />
          )}
        </Permitted>
        <Permitted value={PERM.canViewDailyCashupPersonal}>
          {fromAnyToBool(isExpanded) && (
            <Transfers
              permission="own"
              begin={begin}
              end={end}
              ids={txIds}
              intentIds={intentIds}
              groupName={groupName}
              selectedStylistId={selectedStylistId}
              hideModalForSubPaymentsGroup={hideModalForSubPaymentsGroup}
            />
          )}
        </Permitted>
      </div>
    </div>
  );
};

export default PaymentSource;
